import type { CSSProperties, HTMLAttributes } from 'react';

import maskotRaw from '../../icons/maskot.svg?raw';
import snapshot from '../../ui/icon.snapshot.json' with { type: 'json' };

const files = import.meta.glob('../../icons/*.svg', {
  eager: true,
  import: 'default',
  query: '?url&no-inline',
}) as Record<string, string>;

const urlByName: Record<string, string> = {};

for (const [file, url] of Object.entries(files)) {
  const name = file.replace(/^.*\//, '').replace(/\.svg$/, '');
  urlByName[name] = url;
}

export type IconKitGroup = keyof typeof snapshot.groups;
export type IconName =
  | (typeof snapshot.groups)[IconKitGroup][number]
  | (typeof snapshot.notInFigma)[number];

export interface IconProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  name: IconName;
  size?: number | string;
}

const allowedNames = new Set<string>([
  ...Object.values(snapshot.groups).flat(),
  ...snapshot.notInFigma,
]);

const monochromeNames = new Set<string>([
  ...snapshot.groups.general.filter((name) => !snapshot.preserveColor.includes(name)),
  ...snapshot.groups.digital,
  ...snapshot.notInFigma,
  'logoApple',
  'logoClaude',
  'logoCursor',
  'logoGpt',
]);

export const iconGroups: Record<IconKitGroup, IconName[]> = {
  general: [...snapshot.groups.general],
  digital: [...snapshot.groups.digital],
  colored: [...snapshot.groups.colored],
  logos: [...snapshot.groups.logos],
};

export const iconNames = Object.freeze(Object.values(snapshot.groups).flat()) as IconName[];

interface MaskotPath {
  d: string;
  fill: string;
  transform?: string;
}

let parsedMaskot: { paths: MaskotPath[]; viewBox: string } | undefined;

function getMaskot() {
  if (parsedMaskot) return parsedMaskot;
  const document = new DOMParser().parseFromString(maskotRaw, 'image/svg+xml');
  const root = document.documentElement;
  if (root.tagName !== 'svg' || document.querySelector('parsererror')) {
    throw new Error('Invalid SVG for icon "maskot"');
  }
  const paths = [...root.querySelectorAll('path')].map((path) => {
    const d = path.getAttribute('d');
    const fill = path.getAttribute('fill');
    if (!d || !fill) throw new Error('Invalid path in icon "maskot"');
    const transform = path.getAttribute('transform');
    return { d, fill, ...(transform ? { transform } : {}) };
  });
  parsedMaskot = {
    paths,
    viewBox: root.getAttribute('viewBox') ?? '0 0 16 16',
  };
  return parsedMaskot;
}

function dimensions(size: number | string, style: CSSProperties | undefined): CSSProperties {
  return {
    display: 'inline-block',
    flex: '0 0 auto',
    height: size,
    width: size,
    ...style,
  };
}

export function Icon({
  className = '',
  name,
  size = snapshot.kit.sizePx,
  style,
  ...props
}: IconProps) {
  if (!allowedNames.has(name)) throw new Error(`Unknown icon "${name}"`);
  const url = urlByName[name];
  if (!url) throw new Error(`Missing SVG for icon "${name}"`);

  const commonProps = {
    ...props,
    'aria-hidden': true,
    className: `ui-icon-glyph ${className}`.trim(),
    'data-ui-icon': name,
  } as const;

  if (name === 'maskot') {
    const maskot = getMaskot();
    return (
      <span {...commonProps} style={{ ...dimensions(size, style), lineHeight: 0 }}>
        <svg
          aria-hidden="true"
          focusable="false"
          height="100%"
          viewBox={maskot.viewBox}
          width="100%"
          xmlns="http://www.w3.org/2000/svg"
        >
          {maskot.paths.map((path, index) => (
            <path {...path} key={`${index}:${path.d}`} />
          ))}
        </svg>
      </span>
    );
  }

  if (monochromeNames.has(name)) {
    return (
      <span
        {...commonProps}
        style={{
          ...dimensions(size, style),
          WebkitMaskImage: `url("${url}")`,
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          backgroundColor: 'currentColor',
          maskImage: `url("${url}")`,
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          maskSize: 'contain',
        }}
      />
    );
  }

  return (
    <span {...commonProps} style={{ ...dimensions(size, style), lineHeight: 0 }}>
      <img alt="" draggable={false} height="100%" src={url} width="100%" />
    </span>
  );
}
